import React, { useEffect } from 'react';
import { CheckCircle2, XCircle, AlertCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  type: ToastType;
  title: string;
  message?: string;
  onClose: () => void;
  duration?: number;
}

/**
 * Toast notification component
 * 
 * Design: Dieter Rams - Industrial Refinement
 * - 4px border radii (rounded)
 * - No decorative shadows (shadow-md max)
 */
const Toast: React.FC<ToastProps> = ({ 
  type, 
  title, 
  message, 
  onClose, 
  duration = 5000 
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const icons = {
    success: <CheckCircle2 className="h-5 w-5 text-success" />,
    error: <XCircle className="h-5 w-5 text-destructive" />,
    warning: <AlertCircle className="h-5 w-5 text-warning" />,
    info: <AlertCircle className="h-5 w-5 text-info" />,
  };

  const styles = {
    success: 'bg-background border-success text-success',
    error: 'bg-background border-destructive text-destructive',
    warning: 'bg-background border-warning text-warning',
    info: 'bg-background border-primary text-primary',
  };

  return (
    <div 
      className={`max-w-md w-full animate-in slide-in-from-top-5 ${styles[type]} border-l-4 border rounded shadow-md p-4 transition-all`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 animate-scale-in">
          {icons[type]}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">
            {title}
          </p>
          {message && (
            <p className="mt-1 text-sm opacity-90">
              {message}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 inline-flex text-current opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current rounded p-1 transition-all active:scale-95"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
